import { File } from "../types";
import i18n from "../../../i18n";

function toDate(value?: unknown): Date | undefined {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof value.toDate === "function"
  ) {
    return value.toDate();
  }

  return undefined;
}

export function getFileModifiedTimestamp(
  file: Pick<File, "createdAt" | "updatedAt">,
): number {
  return (toDate(file.updatedAt) ?? toDate(file.createdAt))?.getTime() ?? 0;
}

export function formatModifiedTime(
  file: Pick<File, "createdAt" | "updatedAt">,
): string {
  const timestamp = getFileModifiedTimestamp(file);

  if (!timestamp) return i18n.t("Modified unknown");

  const now = Date.now();
  const diff = now - timestamp;
  const hours = diff / (1000 * 60 * 60);
  const days = hours / 24;

  if (hours < 1) return i18n.t("Modified just now");
  if (hours < 24)
    return i18n.t("Modified {{count}} hours ago", {
      count: Math.floor(hours),
    });
  if (days < 2) return i18n.t("Modified yesterday");
  return i18n.t("Modified {{count}} days ago", {
    count: Math.floor(days),
  });
}

export function getModifiedDisplayValue(file: File): string {
  const timestamp = getFileModifiedTimestamp(file);

  if (timestamp) {
    return i18n.t("Modified {{value}}", {
      value: formatTimestamp(timestamp),
    });
  }

  return i18n.t("Modified unknown");
}

export function getTimestampDisplayValue(value?: Date): string {
  const timestamp = toDate(value)?.getTime() ?? 0;

  if (timestamp) {
    return formatTimestamp(timestamp);
  }

  return "Unknown";
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}
